import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Row, Col, Table } from "react-bootstrap";
import GetData from "../../../services/getData";
import SelectModal from "../../../components/layout/selectModal/SelectModal";
import Toast from "../../../components/layout/toast/Toast";
import "../patients.css";

const DOCUMENT_TYPE_OPTIONS = [
  { id: "1", labelKey: "layout.profileDocs.primary", path: "primary" },
  { id: "2", labelKey: "layout.profileDocs.repeat", path: "repeat" },
  { id: "3", labelKey: "layout.profileDocs.anamnesis", path: "anamnesis" },
  { id: "22", labelKey: "layout.profileDocs.smart-diagnostics", path: "smart-diagnostics" },
];

function formatDateForDisplay(isoDate) {
  if (!isoDate) return "—";
  const [datePart] = String(isoDate).trim().split(/[ T]/);
  const [year, month, day] = datePart.split("-");
  if (!year || !month || !day) return "—";
  return `${day}.${month}.${year}`;
}

function sortDocsNewestFirst(docs) {
  return [...docs].sort((a, b) => {
    const da = Date.parse(a?.created_date) || 0;
    const db = Date.parse(b?.created_date) || 0;
    if (db !== da) return db - da;
    return (Number(b?.id) || 0) - (Number(a?.id) || 0);
  });
}

function PatientProfileList() {
  const { t } = useTranslation("patient");
  const navigate = useNavigate();
  const { patientId } = useParams();
  const [docsList, setDocsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState({});
  const [blocked, setBlocked] = useState(false);
  const [showDocTypeModal, setShowDocTypeModal] = useState(false);
  const [selectedDocTypeId, setSelectedDocTypeId] = useState("");

  const sortedDocsList = useMemo(
    () => sortDocsNewestFirst(docsList),
    [docsList],
  );

  const documentTypes = useMemo(
    () =>
      DOCUMENT_TYPE_OPTIONS.map((option) => ({
        ...option,
        name: t(option.labelKey),
      })),
    [t],
  );

  const loadDocsList = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await GetData({
        module: "patient",
        block: "patient_docs",
        id_patient: patientId,
        action: "list",
      });
      setDocsList(data.result || []);
    } catch (err) {
      console.error("Ошибка загрузки документов медкарты:", err);
      setError(err.message || t("profileList.errors.loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDocsList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  const openDocTypeModal = () => {
    setSelectedDocTypeId("");
    setShowDocTypeModal(true);
  };

  const getDocPath = (doc) => {
    const path =
      doc?.path ||
      documentTypes.find((item) => String(item.id) === String(doc?.template_id))
        ?.path;
    if (!path || !doc?.id) return null;
    return `/patient/${patientId}/profile/${path}/${doc.id}`;
  };

  if (loading) return <div className="loading">{t("loading")}</div>;
  if (error) return <div className="error">{t("error", { ns: "common", message: error })}</div>;

  return (
    <>
      {toast.title ? (
        <Toast key={toast.id} title={toast.title} status={toast.status} />
      ) : (
        false
      )}

      {sortedDocsList.length > 0 ? (
        <Row>
          <Col>
            <div className="table-list">
              <Table className="main-table-list rounded-table">
                <thead>
                  <tr>
                    <th>{t("profileList.colType")}</th>
                    <th>{t("profileList.colCreated")}</th>
                    <th>{t("profileList.colAuthor")}</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedDocsList.map((doc) => {
                    const to = getDocPath(doc);
                    return (
                      <tr key={doc.id}>
                        <td>
                          {to ? (
                            <Link to={to} className="table-item-link">
                              <div className="table-item-name">
                                {doc.template_name || t("profileList.document")}
                              </div>
                            </Link>
                          ) : (
                            <div className="table-item-name">
                              {doc.template_name || t("profileList.document")}
                            </div>
                          )}
                        </td>
                        <td>{formatDateForDisplay(doc.created_date)}</td>
                        <td>{doc.create_user_name || "—"}</td>
                      </tr>
                    );
                  })}
                  <tr className="table-summary">
                    <td colSpan={3} className="summary-cell">
                      {t("profileList.found", { count: sortedDocsList.length })}
                    </td>
                  </tr>
                </tbody>
              </Table>
            </div>
          </Col>
        </Row>
      ) : (
        <div className="no-patients">{t("profileList.empty")}</div>
      )}

      <div className="patient-list-add">
        <button
          type="button"
          className="button-default"
          onClick={openDocTypeModal}
        >
          {t("profileList.add")}
        </button>
      </div>

      <SelectModal
        show={showDocTypeModal}
        onHide={() => {
          setShowDocTypeModal(false);
          setSelectedDocTypeId("");
        }}
        title={t("profileList.selectType")}
        options={documentTypes}
        value={selectedDocTypeId}
        onChange={setSelectedDocTypeId}
        onSelect={async (selected) => {
          if (!selected?.path || blocked) return;
          setBlocked(true);
          try {
            const response = await GetData({
              module: "patient",
              block: "patient_docs",
              id_patient: patientId,
              action: "create",
              template_id: Number(selected.id),
            });

            if (response?.status === "fail") {
              setToast({
                id: Date.now(),
                title: response.message || t("profileList.errors.createFailed"),
                status: "error",
              });
              return;
            }

            const createdDocId =
              response?.result?.id?.value || response?.new_doc_id || null;

            setShowDocTypeModal(false);
            setSelectedDocTypeId("");
            setToast({
              id: Date.now(),
              title: response.message || t("profileList.created"),
              status: response.status || "success",
            });

            await loadDocsList();

            if (createdDocId) {
              navigate(
                `/patient/${patientId}/profile/${selected.path}/${createdDocId}`,
              );
            }
          } catch (err) {
            console.error("Ошибка создания документа медкарты:", err);
            setToast({
              id: Date.now(),
              title: t("profileList.errors.createFailed"),
              status: "error",
            });
          } finally {
            setBlocked(false);
          }
        }}
        optionLabel={(item) => item.name}
        optionValue={(item) => String(item.id)}
        confirmButtonText={t("profileList.create")}
        emptyMessage={t("profileList.noTypes")}
      />
    </>
  );
}

export default PatientProfileList;
